import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { supabase } from './supabase';
import './App.css';

// Import context
import { CartProvider, useCart } from './context/CartContext';
import { CustomerProvider, useCustomer } from './context/CustomerContext';
import { CurrencyProvider, useCurrency } from './context/CurrencyContext';
import { formatPrice } from './utils/currencyUtils';

// Import PayPal config
import { PAYPAL_CONFIG } from './config/paypal';

// Import components
import CartPopup from './components/CartPopup';
import AddToCartNotification from './components/AddToCartNotification';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';

// Import pages
import Products from './pages/Products';
import About from './pages/About';
import Contact from './pages/Contact';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import ThankYou from './pages/ThankYou';
import Electronics from './pages/Electronics';
import Fashion from './pages/Fashion';
import HomeGarden from './pages/HomeGarden';
import Books from './pages/Books';
import Gaming from './pages/Gaming';
import Sports from './pages/Sports';
import Careers from './pages/Careers';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import HelpCenter from './pages/HelpCenter';
import Returns from './pages/Returns';
import ShippingInfo from './pages/ShippingInfo';
import PressReleases from './pages/PressReleases';
import CookiePolicy from './pages/CookiePolicy';
import Security from './pages/Security';
import VendorLogin from './pages/VendorLogin';
import VendorDashboard from './pages/VendorDashboard';
import VendorLayout from './components/VendorLayout';
import VendorResetPassword from './pages/VendorResetPassword';
import CustomerResetPassword from './pages/CustomerResetPassword';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import AdminLayout from './components/AdminLayout';
import CustomerLogin from './pages/CustomerLogin';
import CustomerDashboard from './pages/CustomerDashboard';
import ProductDetail from './pages/ProductDetail';

function App() {
  return (
    <PayPalScriptProvider options={PAYPAL_CONFIG}>
      <CustomerProvider>
      <CartProvider>
        <CurrencyProvider>
                  <Router>
          <ScrollToTop />
          <AppContent />
        </Router>
        </CurrencyProvider>
      </CartProvider>
      </CustomerProvider>
    </PayPalScriptProvider>
  );
}

function AppContent() {
  console.log('📱 AppContent component loading...');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [signInHover, setSignInHover] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { customer, isLoggedIn, logout } = useCustomer();
  const { items } = useCart();
  const { currency, setCurrency } = useCurrency();

  // Function to get country name based on selected currency
  const getCountryForCurrency = () => {
    switch (currency) {
      case '$':
        return 'United States';
      case '£':
        return 'United Kingdom';
      case 'AED':
        return 'UAE';
      case 'KSA':
        return 'Saudi Arabia';
      default:
        return 'United Kingdom';
    }
  };

  // Function to get currency from country name
  const getCurrencyFromCountry = (country) => {
    switch (country) {
      case 'United States':
        return '$';
      case 'United Kingdom':
        return '£';
      case 'UAE':
        return 'AED';
      case 'Saudi Arabia':
        return 'KSA';
      default:
        return '£';
    }
  };

  // Function to handle country change
  const handleCountryChange = (newCountry) => {
    const newCurrency = getCurrencyFromCountry(newCountry);
    setCurrency(newCurrency);
  };

  // Function to toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Close dropdown when clicking outside or pressing escape
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest('.delivery-location-selector') && !event.target.closest('.mobile-delivery-location')) {
        setShowCountryDropdown(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && showCountryDropdown) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showCountryDropdown]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const closeSidebar = () => {
    setIsSidebarOpen(false);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
    }
  };

  const handleSearchInputChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Check if current route is admin or vendor
  const isAdminOrVendorRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/vendor');
  const isHome = location.pathname === '/';

  // Fetch all categories from database and set fixed categories for main menu
  useEffect(() => {
    const fetchAllCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('categories')
          .select('*')
          .eq('status', 'active')
          .order('name', { ascending: true });

        if (error) {
          console.error('Error fetching all categories:', error);
        } else {
          setAllCategories(data || []);
        }
      } catch (error) {
        console.error('Error fetching all categories:', error);
      }
    };

    const fetchSubcategories = async () => {
      try {
        const { data, error } = await supabase
          .from('subcategories')
          .select('*, categories(name)')
          .eq('status', 'active')
          .order('name', { ascending: true });

        if (error) {
          console.error('Error fetching subcategories:', error);
        } else {
          setSubcategories(data || []);
        }
      } catch (error) {
        console.error('Error fetching subcategories:', error);
      }
    };

    // Set fixed categories for main menu
    const setFixedCategories = () => {
      const fixedCategories = [
        { id: 1, name: 'Electronics' },
        { id: 2, name: 'Pet Care' },
        { id: 3, name: 'Home & Kitchen' },
        { id: 4, name: 'Personal Care' }
      ];
      setCategories(fixedCategories);
    };

    // Only fetch and set categories for customer pages
    if (!isAdminOrVendorRoute) {
      fetchAllCategories();
      fetchSubcategories();
      setFixedCategories();
    }
  }, [isAdminOrVendorRoute]);

  return (
    <div className="App">
      {/* Header/Navigation - Only show for customer pages */}
      {!isAdminOrVendorRoute && (
        <header className="header">
          {/* Top Bar */}
          <div className="header-top">
            <div className="header-top-content">
              {/* Left Side */}
              <div className="header-left">
                <div className="logo">
                  <Link to="/" onClick={closeMobileMenu}>
                    <h1>Inifiy</h1>
                  </Link>
                </div>
                <div className="mobile-cart-section">
                  <div className="currency-converter mobile-currency">
                    <select 
                      value={currency} 
                      onChange={(e) => setCurrency(e.target.value)}
                      className="currency-select mobile-currency-select"
                    >
                      <option value="$">$</option>
                      <option value="£">£</option>
                      <option value="AED">AED</option>
                      <option value="KSA">KSA</option>
                    </select>
                  </div>
                  <Link to="/cart" className="cart-icon-container mobile-cart-icon">
                    <div className="cart-icon-wrapper">
                      <svg className="cart-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z"/>
                        <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z"/>
                        <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6"/>
                      </svg>
                      {items.length > 0 && (
                        <span className="cart-badge">{items.length}</span>
                      )}
                    </div>
                    <span className="cart-text">Cart</span>
                  </Link>
                </div>
              </div>

              {/* Middle Section - Search Bar */}
              <div className="search-section">
                <form onSubmit={handleSearch} className="search-bar">
                  <div className="search-category">
                    <span>All</span>
                    <span className="dropdown-arrow">▼</span>
                  </div>
                  <input 
                    type="text" 
                    placeholder="Search Inifiy" 
                    className="search-input"
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                  />
                  <button type="submit" className="search-btn">
                    <span className="search-icon">🔍</span>
                  </button>
                </form>
                
                {/* Mobile Menu Button */}
                <div className="mobile-menu-button-container">
                  <div className="mobile-menu-row">
                    <button className="mobile-menu-button" onClick={toggleSidebar}>
                      <span className="mobile-menu-icon">☰</span>
                      <span className="mobile-menu-text">All</span>
                    </button>
                    
                    {/* Mobile Delivery Location - Centered */}
                    <div className="mobile-delivery-location">
                      <span className="mobile-delivery-text">Deliver to {getCountryForCurrency()}</span>
                      <button 
                        className="mobile-delivery-edit-btn"
                        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      >
                        ✏️
                      </button>
                      
                      {/* Mobile Country Dropdown */}
                      {showCountryDropdown && (
                        <div className="mobile-country-dropdown">
                          <div className="country-option" onClick={() => handleCountryChange('United States')}>
                            🇺🇸 United States
                          </div>
                          <div className="country-option" onClick={() => handleCountryChange('United Kingdom')}>
                            🇬🇧 United Kingdom
                          </div>
                          <div className="country-option" onClick={() => handleCountryChange('UAE')}>
                            🇦🇪 UAE
                          </div>
                          <div className="country-option" onClick={() => handleCountryChange('Saudi Arabia')}>
                            🇸🇦 Saudi Arabia
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="mobile-dropdown-container">
                      <button className="mobile-nav-toggle" onClick={toggleMobileMenu}>
                        <span className="mobile-nav-icon">☰</span>
                      </button>
                      {isMobileMenuOpen && (
                        <div className="mobile-dropdown-menu">
                          <Link to="/" onClick={closeMobileMenu} className="dropdown-item">Home</Link>
                          <Link to="/products?category=Electronics" onClick={closeMobileMenu} className="dropdown-item">Electronics</Link>
                          <Link to="/products?category=Pet Care" onClick={closeMobileMenu} className="dropdown-item">Pet Care</Link>
                          <Link to="/products?category=Home & Kitchen" onClick={closeMobileMenu} className="dropdown-item">Home & Kitchen</Link>
                          <Link to="/products?category=Personal Care" onClick={closeMobileMenu} className="dropdown-item">Personal Care</Link>
                          {isLoggedIn ? (
                            <Link to="/dashboard" onClick={closeMobileMenu} className="dropdown-item">My Account</Link>
                          ) : (
                            <Link to="/login" onClick={closeMobileMenu} className="dropdown-item">Sign In</Link>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Side */}
              <div className="header-right">
                <div className="currency-converter">
                  <select 
                    value={currency} 
                    onChange={(e) => setCurrency(e.target.value)}
                    className="currency-select"
                  >
                    <option value="$">$ USD</option>
                    <option value="£">£ GBP</option>
                    <option value="AED">AED</option>
                    <option value="KSA">KSA</option>
                  </select>
                </div>
                <Link to="/cart" className="cart-icon-container desktop-cart">
                  <div className="cart-icon-wrapper">
                    <svg className="cart-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z"/>
                      <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z"/>
                      <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6"/>
                    </svg>
                    {items.length > 0 && (
                      <span className="cart-badge">{items.length}</span>
                    )}
                  </div>
                  <span className="cart-text">Cart</span>
                </Link>
                {isLoggedIn ? (
                  <Link 
                    to="/dashboard" 
                    className="desktop-signin-link"
                    style={{
                      color: signInHover ? 'white' : '#232f3e',
                      textDecoration: 'none',
                      fontWeight: '600',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease',
                      border: '2px solid #232f3e',
                      backgroundColor: signInHover ? '#232f3e' : 'white',
                      display: 'inline-block',
                      boxShadow: signInHover ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      transform: signInHover ? 'translateY(-2px)' : 'translateY(0)'
                    }}
                    onMouseEnter={() => setSignInHover(true)}
                    onMouseLeave={() => setSignInHover(false)}
                  >My Account</Link>
                ) : (
                  <Link 
                    to="/login" 
                    className="desktop-signin-link"
                    style={{
                      color: signInHover ? 'white' : '#232f3e',
                      textDecoration: 'none',
                      fontWeight: '600',
                      padding: '10px 20px',
                      borderRadius: '6px',
                      transition: 'all 0.3s ease',
                      border: '2px solid #232f3e',
                      backgroundColor: signInHover ? '#232f3e' : 'white',
                      display: 'inline-block',
                      boxShadow: signInHover ? '0 4px 8px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
                      fontSize: '14px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      transform: signInHover ? 'translateY(-2px)' : 'translateY(0)'
                    }}
                    onMouseEnter={() => setSignInHover(true)}
                    onMouseLeave={() => setSignInHover(false)}
                  >Sign In</Link>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Bar - Navigation */}
          <div className="header-bottom">
            <div className="header-bottom-content">
              <div className="nav-left">
                <button className="menu-toggle" onClick={toggleSidebar}>
                  <span className="menu-icon">☰</span>
                  <span>All</span>
                </button>
              </div>
              
              <nav className={`nav-menu ${isMobileMenuOpen ? 'active' : ''}`}>
                <div className="nav-links">
                  <Link to="/" onClick={closeMobileMenu}>Home</Link>
                  {categories.map(category => (
                    <Link 
                      key={category.id} 
                      to={`/products?category=${encodeURIComponent(category.name)}`} 
                      onClick={closeMobileMenu}
                    >
                      {category.name}
                    </Link>
                  ))}
                  {isLoggedIn && (
                    <button onClick={() => {
                      logout();
                      closeMobileMenu();
                    }} className="nav-link logout-btn">Logout</button>
                  )}
                </div>
              </nav>

              <div className="nav-right">
                <div className="delivery-location-selector">
                  <span className="free-delivery">
                    <strong>Deliver to {getCountryForCurrency()}</strong>
                  </span>
                  <button 
                    className="edit-country-btn"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    title="Change delivery location"
                  >
                    ✏️
                  </button>
                  {showCountryDropdown && (
                    <div className="country-dropdown">
                      <div 
                        className="country-option"
                        onClick={() => {
                          handleCountryChange('United States');
                          setShowCountryDropdown(false);
                        }}
                      >
                        🇺🇸 United States
                      </div>
                      <div 
                        className="country-option"
                        onClick={() => {
                          handleCountryChange('United Kingdom');
                          setShowCountryDropdown(false);
                        }}
                      >
                        🇬🇧 United Kingdom
                      </div>
                      <div 
                        className="country-option"
                        onClick={() => {
                          handleCountryChange('UAE');
                          setShowCountryDropdown(false);
                        }}
                      >
                        🇦🇪 UAE
                      </div>
                      <div 
                        className="country-option"
                        onClick={() => {
                          handleCountryChange('Saudi Arabia');
                          setShowCountryDropdown(false);
                        }}
                      >
                        🇸🇦 Saudi Arabia
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Menu Backdrop */}
          {isMobileMenuOpen && (
            <div className="mobile-menu-backdrop" onClick={closeMobileMenu}></div>
          )}

          {/* Categories Sidebar */}
          {isSidebarOpen && (
            <div className="sidebar-overlay" onClick={closeSidebar}>
              <div className="categories-sidebar" onClick={(e) => e.stopPropagation()}>
                <div className="sidebar-header">
                  <h3>Shop by Department</h3>
                  <button className="sidebar-close" onClick={closeSidebar}>×</button>
                </div>
                <div className="sidebar-content">
                  {allCategories.map(category => {
                    // Find subcategories for this category
                    const categorySubcategories = subcategories.filter(
                      sub => sub.category_id === category.id
                    );
                    const isExpanded = expandedCategories.has(category.id);
                    
                    return (
                      <div key={category.id} className="sidebar-category-group">
                        <div 
                          className="sidebar-category-item"
                          onClick={() => {
                            if (categorySubcategories.length > 0) {
                              // Toggle subcategory visibility
                              toggleCategoryExpansion(category.id);
                            } else {
                              // No subcategories, navigate directly
                              navigate(`/products?category=${encodeURIComponent(category.name)}`);
                              closeSidebar();
                            }
                          }}
                        >
                          {category.name}
                          {categorySubcategories.length > 0 && (
                            <span className={`category-arrow ${isExpanded ? 'rotated' : ''}`}>
                              ▼
                            </span>
                          )}
                        </div>
                        
                        {/* Show subcategories if they exist and are expanded */}
                        {categorySubcategories.length > 0 && (
                          <div className={`sidebar-subcategories ${isExpanded ? 'show' : ''}`}>
                            {categorySubcategories.map(subcategory => (
                              <Link 
                                key={subcategory.id} 
                                to={`/products?category=${encodeURIComponent(category.name)}&subcategory=${encodeURIComponent(subcategory.name)}`} 
                                onClick={closeSidebar}
                                className="sidebar-subcategory-item"
                              >
                                {subcategory.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  <div className="sidebar-footer">
                    <span>See all</span>
                    <span className="dropdown-arrow">▼</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </header>
      )}

      {/* Main Content */}
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/products" element={<Products />} />
          <Route path="/product/:productId" element={<ProductDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/thank-you/:orderId" element={<ThankYou />} />
          <Route path="/electronics" element={<Electronics />} />
          <Route path="/fashion" element={<Fashion />} />
          <Route path="/home-garden" element={<HomeGarden />} />
          <Route path="/books" element={<Books />} />
          <Route path="/gaming" element={<Gaming />} />
          <Route path="/sports" element={<Sports />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/returns" element={<Returns />} />
          <Route path="/shipping" element={<ShippingInfo />} />
          <Route path="/press" element={<PressReleases />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/security" element={<Security />} />
          <Route path="/vendor" element={<VendorLayout />}>
            <Route path="login" element={<VendorLogin />} />
            <Route path="dashboard" element={<VendorDashboard />} />
          </Route>
          <Route path="/vendor/reset-password" element={<VendorResetPassword />} />
        <Route path="/customer/reset-password" element={<CustomerResetPassword />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="login" element={<AdminLogin />} />
            <Route path="dashboard" element={<AdminDashboard />} />
          </Route>
          <Route path="/login" element={<CustomerLogin />} />
          <Route path="/dashboard" element={<CustomerDashboard />} />
        </Routes>
      </main>

      
      {/* Cart Popup - Only show for customer pages */}
      {!isAdminOrVendorRoute && <CartPopup />}
      
      {/* Add to Cart Notification - Only show for customer pages */}
      {!isAdminOrVendorRoute && <AddToCartNotification />}
      
      {/* Footer - Only show for customer pages */}
      {!isAdminOrVendorRoute && <Footer />}
    </div>
  );
}


// HotDealsSection Component
function HotDealsSection({ type, title }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currency } = useCurrency();

  // Function to get the correct price based on selected currency
  const getPriceForCurrency = (product, priceType = 'current') => {
    if (currency === '$') {
      return priceType === 'current' ? (product.price_usd || 0) : (product.original_price_usd || 0);
    } else if (currency === '£') {
      return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
    } else if (currency === 'AED') {
      return priceType === 'current' ? (product.price_aed || 0) : (product.original_price_aed || 0);
    } else if (currency === 'KSA') {
      return priceType === 'current' ? (product.price_sar || 0) : (product.original_price_sar || 0);
    }
    // Default fallback to GBP
    return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
  };

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };
  
  // Fetch products based on type
  useEffect(() => {
        const fetchHotDealsProducts = async () => {
      try {
        let filterField = '';
        let filterValue = true;
        
        // Set the filter based on type
        switch (type) {
          case 'hot_products':
            filterField = 'is_hot_product';
            break;
          case 'todays_deals':
            filterField = 'is_todays_deal';
            break;
          case 'new_arrivals':
            filterField = 'is_new_arrival';
            break;
          case 'exclusive_offers':
            filterField = 'is_exclusive_offer';
            break;
          default:
            filterField = 'is_hot_product';
        }

        // Get the currency code for filtering
        let currencyCode = '';
        if (currency === '$') currencyCode = 'USD';
        else if (currency === '£') currencyCode = 'GBP';
        else if (currency === 'AED') currencyCode = 'AED';
        else if (currency === 'KSA') currencyCode = 'SAR';
        else currencyCode = 'GBP'; // Default fallback

        // First, fetch products that are pinned by admin for this section
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .eq(filterField, filterValue)
          .order('created_at', { ascending: false })
          .limit(20); // Fetch more to filter by currency

        if (error) {
          console.error(`Error fetching ${type} products:`, error);
          setProducts([]);
        } else {
          // Filter products by currency availability
          let filteredProducts = data || [];
          
          // Filter by currency availability
          filteredProducts = filteredProducts.filter(product => {
            let hasPriceInCurrency = false;
            switch (currencyCode) {
              case 'USD':
                hasPriceInCurrency = product.price_usd && parseFloat(product.price_usd) > 0;
                break;
              case 'GBP':
                hasPriceInCurrency = product.price_gbp && parseFloat(product.price_gbp) > 0;
                break;
              case 'AED':
                hasPriceInCurrency = product.price_aed && parseFloat(product.price_aed) > 0;
                break;
              case 'SAR':
                hasPriceInCurrency = product.price_sar && parseFloat(product.price_sar) > 0;
                break;
              default:
                hasPriceInCurrency = true;
            }
            return hasPriceInCurrency;
          });

          // Limit to 4 products for display
          filteredProducts = filteredProducts.slice(0, 4);
          
          console.log(`Found ${filteredProducts.length} ${type} products with ${currencyCode} pricing`);
          setProducts(filteredProducts);
        }
      } catch (error) {
        console.error(`Error fetching ${type} products:`, error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchHotDealsProducts();
  }, [type, currency]);

  if (loading) {
    return (
      <div className="hot-deals-card">
        <h3>{title}</h3>
        <div className="loading-products">Loading...</div>
      </div>
    );
  }

  return (
    <div className="hot-deals-card">
      <h3>{title}</h3>
      {products.length === 0 ? (
        <div className="no-products">
          <p>No products available</p>
        </div>
      ) : (
        <div className="hot-deals-products">
          {products.map((product) => (
            <Link 
              key={product.id} 
              to={`/product/${product.id}`} 
              className="hot-deal-product"
            >
              <div className="product-image">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    loading="lazy"
                  />
                ) : (
                  <div className="product-placeholder">
                    <span>📦</span>
                  </div>
                )}
              </div>
              <div className="product-title-second-word">
                {product.name && product.name.split(' ').length > 1 
                  ? product.name.split(' ')[1] 
                  : product.name}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// CategoryProductGrid Component
function CategoryProductGrid({ category, title }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currency } = useCurrency();

  // Function to get the correct price based on selected currency
  const getPriceForCurrency = (product, priceType = 'current') => {
    if (currency === '$') {
      return priceType === 'current' ? (product.price_usd || 0) : (product.original_price_usd || 0);
    } else if (currency === '£') {
      return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
    } else if (currency === 'AED') {
      return priceType === 'current' ? (product.price_aed || 0) : (product.original_price_aed || 0);
    } else if (currency === 'KSA') {
      return priceType === 'current' ? (product.price_sar || 0) : (product.original_price_sar || 0);
    }
    // Default fallback to GBP
    return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
  };

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };
  
  // Fetch pinned products for specific category
  useEffect(() => {
    const fetchCategoryProducts = async () => {
      try {
        // Get the currency code for filtering
        let currencyCode = '';
        if (currency === '$') currencyCode = 'USD';
        else if (currency === '£') currencyCode = 'GBP';
        else if (currency === 'AED') currencyCode = 'AED';
        else if (currency === 'KSA') currencyCode = 'SAR';
        else currencyCode = 'GBP'; // Default fallback

        // Fetch only products that are pinned for this category and currency
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .eq('is_featured_category', true) // Only pinned products
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          console.error(`Error fetching ${category} pinned products:`, error);
        } else {
          console.log(`Searching for pinned category: "${category}"`);
          console.log(`Found ${data?.length || 0} total pinned products`);
          
          // Filter products by category using flexible matching
          let categoryProducts = data || [];
          if (category && category !== 'all') {
            categoryProducts = categoryProducts.filter(product => {
              const productCategory = product.category?.toLowerCase().trim();
              const selectedCat = category.toLowerCase().trim();
              
              // Exact match
              let matches = productCategory === selectedCat;
              
              // If no exact match, try partial matching for similar categories
              if (!matches) {
                // Handle variations like "Pet Care" vs "Pet Supplies"
                if (selectedCat.includes('pet') || productCategory.includes('pet')) {
                  matches = selectedCat.includes('pet') && productCategory.includes('pet');
                }
                // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
                else if (selectedCat.includes('home') || productCategory.includes('home')) {
                  matches = selectedCat.includes('home') && productCategory.includes('home');
                }
                // Handle variations like "Personal Care" vs "Personal"
                else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
                  matches = selectedCat.includes('personal') && productCategory.includes('personal');
                }
                // Handle variations like "Electronics" vs "Electronic"
                else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
                  matches = selectedCat.includes('electronic') && productCategory.includes('electronic');
                }
              }
              
              if (matches) {
                console.log(`✅ CategoryProductGrid - Pinned product "${product.name}" category "${productCategory}" matches "${selectedCat}"`);
              } else {
                console.log(`❌ CategoryProductGrid - Pinned product "${product.name}" category "${productCategory}" doesn't match "${selectedCat}"`);
              }
              return matches;
            });
          }
          
          console.log(`Found ${categoryProducts?.length || 0} pinned products for ${category}:`, categoryProducts);
          
          if (categoryProducts && categoryProducts.length > 0) {
            console.log('Pinned product categories found:', categoryProducts.map(p => p.category));
          }
          
          // Filter by currency availability - only show products with prices in the selected currency
          let filteredProducts = categoryProducts || [];
          console.log(`CategoryProductGrid - Raw pinned data for ${category}:`, categoryProducts?.length || 0, 'products');
          
          if (currencyCode && categoryProducts.length > 0) {
            filteredProducts = filteredProducts.filter(product => {
              // Check if product has a price in the selected currency
              let hasPriceInCurrency = false;
              switch (currencyCode) {
                case 'USD':
                  hasPriceInCurrency = product.price_usd && parseFloat(product.price_usd) > 0;
                  break;
                case 'GBP':
                  hasPriceInCurrency = product.price_gbp && parseFloat(product.price_gbp) > 0;
                  break;
                case 'AED':
                  hasPriceInCurrency = product.price_aed && parseFloat(product.price_aed) > 0;
                  break;
                case 'SAR':
                  hasPriceInCurrency = product.price_sar && parseFloat(product.price_sar) > 0;
                  break;
                default:
                  hasPriceInCurrency = true;
              }
              
              console.log(`CategoryProductGrid - Pinned product "${product.name}" has ${currencyCode} price: ${hasPriceInCurrency}`);
              return hasPriceInCurrency;
            });
            console.log(`CategoryProductGrid - ${category}: ${filteredProducts.length} pinned products with ${currencyCode} pricing`);
          } else {
            console.log(`CategoryProductGrid - No currency selected, showing all ${filteredProducts.length} pinned products for ${category}`);
          }
          
          // Only show products if we have actual pinned products for this category
          if (filteredProducts.length === 0) {
            console.log(`CategoryProductGrid - No pinned products available for ${category} in selected currency (${currencyCode}), hiding section`);
            setProducts([]);
          } else {
            console.log(`CategoryProductGrid - Found ${filteredProducts.length} pinned products for ${category}`);
            setProducts(filteredProducts);
          }
        }
      } catch (error) {
        console.error(`Error fetching ${category} pinned products:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryProducts();
  }, [category, currency]);
  
  if (loading) {
    return (
      <section className="category-grid-section">
        <div className="container">
          <h2>{title}</h2>
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading {title} products...</p>
          </div>
        </div>
      </section>
    );
  }
  
  if (products.length === 0) {
    return null; // Don't show section if no products available in this country
  }
  
  // Limit products to 4 for display
  const limitedProducts = products.slice(0, 4);
  
  return (
    <section className="category-grid-section">
      <div className="container">
        <h2>{title}</h2>
        <div className="category-grid">
          {limitedProducts.map((product) => (
            <Link 
              key={product.id} 
              to={`/product/${product.id}`} 
              className="category-product-card"
            >
              <div className="category-product-image">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    loading="lazy"
                  />
                ) : (
                  <div className="category-product-placeholder">
                    <span>📦</span>
                  </div>
                )}
              </div>
              <div className="product-title-second-word">
                {product.name && product.name.split(' ').length > 1 
                  ? product.name.split(' ')[1] 
                  : product.name}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// CategoryShowcase Component - New layout for categories
function CategoryShowcase({ category, title }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currency } = useCurrency();

  // Function to get the correct price based on selected currency
  const getPriceForCurrency = (product, priceType = 'current') => {
    if (currency === '$') {
      return priceType === 'current' ? (product.price_usd || 0) : (product.original_price_usd || 0);
    } else if (currency === '£') {
      return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
    } else if (currency === 'AED') {
      return priceType === 'current' ? (product.price_aed || 0) : (product.original_price_aed || 0);
    } else if (currency === 'KSA') {
      return priceType === 'current' ? (product.price_sar || 0) : (product.original_price_sar || 0);
    }
    // Default fallback to GBP
    return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
  };

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };
  
  // Fetch products for specific category that are pinned by admin
  useEffect(() => {
    const fetchCategoryProducts = async () => {
      try {
        // Get the currency code for filtering
        let currencyCode = '';
        if (currency === '$') currencyCode = 'USD';
        else if (currency === '£') currencyCode = 'GBP';
        else if (currency === 'AED') currencyCode = 'AED';
        else if (currency === 'KSA') currencyCode = 'SAR';
        else currencyCode = 'GBP'; // Default fallback

        // Fetch only products that are pinned for this category and currency
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .eq('is_featured_category', true) // Only pinned products
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) {
          console.error(`Error fetching ${category} products:`, error);
        } else {
          // Filter products by category using flexible matching
          let categoryProducts = data || [];
          if (category && category !== 'all') {
            console.log(`CategoryShowcase - Filtering for category: "${category}"`);
            console.log(`CategoryShowcase - Total pinned products before filtering: ${categoryProducts.length}`);
            
            categoryProducts = categoryProducts.filter(product => {
              const productCategory = product.category?.toLowerCase().trim();
              const selectedCat = category.toLowerCase().trim();
              
              // Exact match
              let matches = productCategory === selectedCat;
              
              // If no exact match, try partial matching for similar categories
              if (!matches) {
                // Handle variations like "Pet Care" vs "Pet Supplies"
                if (selectedCat.includes('pet') || productCategory.includes('pet')) {
                  matches = selectedCat.includes('pet') && productCategory.includes('pet');
                }
                // Handle variations like "Home & Kitchen" vs "Home and Kitchen"
                else if (selectedCat.includes('home') || productCategory.includes('home')) {
                  matches = selectedCat.includes('home') && productCategory.includes('home');
                }
                // Handle variations like "Personal Care" vs "Personal"
                else if (selectedCat.includes('personal') || productCategory.includes('personal')) {
                  matches = selectedCat.includes('personal') && productCategory.includes('personal');
                }
                // Handle variations like "Electronics" vs "Electronic"
                else if (selectedCat.includes('electronic') || productCategory.includes('electronic')) {
                  matches = selectedCat.includes('electronic') && productCategory.includes('electronic');
                }
              }
              
              if (matches) {
                console.log(`✅ CategoryShowcase - Pinned product "${product.name}" category "${productCategory}" matches "${selectedCat}"`);
              } else {
                console.log(`❌ CategoryShowcase - Pinned product "${product.name}" category "${productCategory}" doesn't match "${selectedCat}"`);
              }
              
              return matches;
            });
            
            console.log(`CategoryShowcase - Pinned products after category filtering: ${categoryProducts.length}`);
          }
          
          // Filter by currency availability - only show products with prices in the selected currency
          let filteredProducts = categoryProducts || [];
          console.log(`CategoryShowcase - Raw pinned data for ${category}:`, categoryProducts?.length || 0, 'products');
          
          if (currencyCode && categoryProducts.length > 0) {
            filteredProducts = filteredProducts.filter(product => {
              // Check if product has a price in the selected currency
              let hasPriceInCurrency = false;
              switch (currencyCode) {
                case 'USD':
                  hasPriceInCurrency = product.price_usd && parseFloat(product.price_usd) > 0;
                  break;
                case 'GBP':
                  hasPriceInCurrency = product.price_gbp && parseFloat(product.price_gbp) > 0;
                  break;
                case 'AED':
                  hasPriceInCurrency = product.price_aed && parseFloat(product.price_aed) > 0;
                  break;
                case 'SAR':
                  hasPriceInCurrency = product.price_sar && parseFloat(product.price_sar) > 0;
                  break;
                default:
                  hasPriceInCurrency = true;
              }
              
              console.log(`CategoryShowcase - Pinned product "${product.name}" has ${currencyCode} price: ${hasPriceInCurrency}`);
              return hasPriceInCurrency;
            });
            console.log(`CategoryShowcase - ${category}: ${filteredProducts.length} pinned products with ${currencyCode} pricing`);
          } else {
            console.log(`CategoryShowcase - No currency selected, showing all ${filteredProducts.length} pinned products for ${category}`);
          }
          
          // Only show products if we have actual pinned products for this category
          if (filteredProducts.length === 0) {
            console.log(`CategoryShowcase - No pinned products available for ${category} in selected currency (${currencyCode}), hiding category`);
            setProducts([]);
          } else {
            console.log(`CategoryShowcase - Found ${filteredProducts.length} pinned products for ${category}`);
            setProducts(filteredProducts);
          }
        }
      } catch (error) {
        console.error(`Error fetching ${category} pinned products:`, error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategoryProducts();
  }, [category, currency]);
  
  if (loading) {
    return (
      <div className="category-showcase">
        <h3 className="category-showcase-title">{title}</h3>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  
  if (products.length === 0) {
    // Don't show category if no products are available
    return null;
  }
  
  // Limit products to 4 for display
  const limitedProducts = products.slice(0, 4);
  
  // Function to get category URL path
  const getCategoryPath = (categoryName) => {
    // All categories should use the products page with category parameter
    return `/products?category=${encodeURIComponent(categoryName)}`;
  };

  return (
    <div className="category-showcase">
      <h3 className="category-showcase-title">{title}</h3>
              <div className="category-showcase-products-grid">
          {limitedProducts.map((product) => (
            <Link 
              key={product.id} 
              to={`/product/${product.id}`} 
              className="category-showcase-product"
            >
              <div className="category-showcase-image">
                {product.image_url ? (
                  <img 
                    src={product.image_url} 
                    alt={product.name}
                    loading="lazy"
                  />
                ) : (
                  <div className="category-showcase-placeholder">
                    <span>📦</span>
                  </div>
                )}
              </div>
              <div className="product-title-second-word">
                {product.name && product.name.split(' ').length > 1 
                  ? product.name.split(' ')[1] 
                  : product.name}
              </div>
            </Link>
          ))}
        </div>
      <div className="category-showcase-footer">
        <Link 
          to={getCategoryPath(title)} 
          className="discover-more-btn"
        >
          Discover More
        </Link>
      </div>
    </div>
  );
}

// HeroSlider Component
function HeroSlider() {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const slides = [
    {
      id: 1,
      image: 'https://oatwrcgucondsxooeyuk.supabase.co/storage/v1/object/sign/slider-images/BUY-NOW.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wNWM0NTNjNy02Y2RkLTQyN2UtOTdmOC00NjNhMWI1ZWY2YTMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJzbGlkZXItaW1hZ2VzL0JVWS1OT1cucG5nIiwiaWF0IjoxNzU2MTQwMjA1LCJleHAiOjMwMTc1ODAyMDV9.2PEKguwLqGMd5PYEryQLSU_RrmtWM1Bqxs-MfEc9lt0',
      alt: 'Promotional Banner 1'
    },
    {
      id: 2,
      image: 'https://oatwrcgucondsxooeyuk.supabase.co/storage/v1/object/sign/slider-images/BUY-NOW-1.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wNWM0NTNjNy02Y2RkLTQyN2UtOTdmOC00NjNhMWI1ZWY2YTMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJzbGlkZXItaW1hZ2VzL0JVWS1OT1ctMS5wbmciLCJpYXQiOjE3NTYxNDAxMjcsImV4cCI6MTc1OTU5NjEyN30.fYC_gOH2CoJfJp_rrtYwDjix7aheEYL7jWFYqgm0G-M',
      alt: 'Promotional Banner 2'
    },
    {
      id: 3,
      image: 'https://oatwrcgucondsxooeyuk.supabase.co/storage/v1/object/sign/slider-images/BUY-NOW-2.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wNWM0NTNjNy02Y2RkLTQyN2UtOTdmOC00NjNhMWI1ZWY2YTMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJzbGlkZXItaW1hZ2VzL0JVWS1OT1ctMi5wbmciLCJpYXQiOjE3NTYxNDAxNzAsImV4cCI6MzAxNzU4MDE3MH0.cDzODu902conWrxauFLFtyb9EUgpdnADTcghUpthaU8',
      alt: 'Promotional Banner 3'
    },
    {
      id: 4,
      image: 'https://oatwrcgucondsxooeyuk.supabase.co/storage/v1/object/sign/slider-images/BUY-NOW-3.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV8wNWM0NTNjNy02Y2RkLTQyN2UtOTdmOC00NjNhMWI1ZWY2YTMiLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJzbGlkZXItaW1hZ2VzL0JVWS1OT1ctMy5wbmciLCJpYXQiOjE3NTYxNDAxODgsImV4cCI6MzAxNzU4MDE4OH0.KRw-FonwHObJq2cMh9ekULoyEOKPCtU_0mK9roCvWhQ',
      alt: 'Promotional Banner 4'
    }
  ];

  // Debug: Log the image URLs to check if they're correct
  useEffect(() => {
    console.log('Slider images:', slides.map(slide => slide.image));
  }, []);

  console.log('Current slide:', currentSlide);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // Auto-play functionality
  useEffect(() => {
    const timer = setInterval(nextSlide, 5000); // Change slide every 5 seconds
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="hero-slider">
      <div className="slider-container">
        <div className="slider-wrapper" style={{ transform: `translateX(-${currentSlide * 25}%)` }}>
          {slides.map((slide) => (
            <div key={slide.id} className="slide">
              <img 
                src={slide.image} 
                alt={slide.alt} 
                loading="lazy"
                onError={(e) => {
                  console.error('Image failed to load:', slide.image);
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="image-fallback" style={{ display: 'none', width: '100%', height: '100%', background: '#f0f0f0', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
                <span>Image failed to load</span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Navigation Arrows */}
        <button className="slider-nav prev" onClick={prevSlide}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
        <button className="slider-nav next" onClick={nextSlide}>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </button>
        
        {/* Slide Indicators */}
        <div className="slider-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`indicator ${index === currentSlide ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

// HomePage Component (moved from main App component)
function HomePage() {
  console.log('🏠 HomePage component loading...');
  const { addToCart } = useCart();
  const { currency } = useCurrency();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroBackgroundImage, setHeroBackgroundImage] = useState('https://oatwrcgucondsxooeyuk.supabase.co/storage/v1/object/public/site-images/hero-bg-1735489002207.png');
  
  console.log('🖼️ Current heroBackgroundImage state:', heroBackgroundImage);


  // Function to get the correct price based on selected currency
  const getPriceForCurrency = (product, priceType = 'current') => {
    if (currency === '$') {
      return priceType === 'current' ? (product.price_usd || 0) : (product.original_price_usd || 0);
    } else if (currency === '£') {
      return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
    } else if (currency === 'AED') {
      return priceType === 'current' ? (product.price_aed || 0) : (product.original_price_aed || 0);
    } else if (currency === 'KSA') {
      return priceType === 'current' ? (product.price_sar || 0) : (product.original_price_sar || 0);
    }
    // Default fallback to GBP
    return priceType === 'current' ? (product.price_gbp || 0) : (product.original_price_gbp || 0);
  };

  // Function to get currency symbol
  const getCurrencySymbol = () => {
    if (currency === '$') return '$';
    else if (currency === '£') return '£';
    else if (currency === 'AED') return 'د.إ';
    else if (currency === 'KSA') return 'ر.س';
    return '£'; // Default fallback
  };
  
  // Load site settings for hero background
  useEffect(() => {
    const loadSiteSettings = async () => {
      console.log('🔄 Loading site settings for hero background...');
      try {
        const { data, error } = await supabase
          .from('site_settings')
          .select('*')
          .eq('setting_key', 'hero_background_image')
          .order('updated_at', { ascending: false })
          .limit(1);
        
        if (error) {
          console.log('❌ Error loading site_settings:', error);
          console.log('🔧 Creating default hero background setting...');
          
          // Try to create the default setting
          const { data: insertData, error: insertError } = await supabase
            .from('site_settings')
            .insert({
              setting_key: 'hero_background_image',
              setting_value: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
              setting_type: 'image_url',
              description: 'Background image for the hero section on homepage'
            })
            .select()
            .single();
            
          if (insertError) {
            console.log('❌ Error creating default setting:', insertError);
            setHeroBackgroundImage('https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80');
          } else {
            console.log('✅ Created default setting:', insertData);
            setHeroBackgroundImage(insertData.setting_value);
          }
        } else if (data && data.length > 0) {
          console.log('✅ Loaded hero background from database:', data[0].setting_value);
          setHeroBackgroundImage(data[0].setting_value || '');
        } else {
          console.log('⚠️ No hero background setting found');
          setHeroBackgroundImage('https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80');
        }
      } catch (error) {
        console.error('💥 Error loading site settings:', error);
        // Use default image on error
        setHeroBackgroundImage('https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80');
      }
    };

    loadSiteSettings();

    // Listen for background image updates (for real-time refresh)
    const handleStorageChange = () => {
      loadSiteSettings();
    };

    window.addEventListener('hero-background-updated', handleStorageChange);
    
    return () => {
      window.removeEventListener('hero-background-updated', handleStorageChange);
    };
  }, []);

  // Fetch featured products from Supabase
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('status', 'active')
          .eq('featured', true)
          .order('created_at', { ascending: false })
          .limit(30);

        if (error) {
          console.error('Error fetching featured products:', error);
        } else {
          // Filter by country availability only (show all products available in the country)
          let filteredProducts = data || [];
          if (currency) {
            let targetCountry = '';
            if (currency === '$') targetCountry = 'USA';
            else if (currency === '£') targetCountry = 'UK';
            else if (currency === 'AED') targetCountry = 'UAE';
            else if (currency === 'KSA') targetCountry = 'Saudi Arabia';
            
            if (targetCountry) {
              filteredProducts = filteredProducts.filter(product => {
                const countries = product.countries || [];
                const isAvailableInCountry = countries.includes(targetCountry);
                
                console.log(`Featured products - Product "${product.name}" countries: [${countries.join(', ')}], Target: ${targetCountry}, Available: ${isAvailableInCountry}`);
                return isAvailableInCountry;
              });
              console.log(`Featured products - Filtered to ${filteredProducts.length} products available in ${targetCountry}`);
            }
          }
          
          setFeaturedProducts(filteredProducts);
        }
      } catch (error) {
        console.error('Error fetching featured products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, [currency]);
  
  const handleAddToCart = (product) => {
    addToCart(product);
  };


  
  return (
    <>
      {/* Hero Slider */}
      <HeroSlider />

      {/* Hot Products & Deals Section */}
      <section className="hot-products-deals">
        <div className="container">
          <div className="hot-deals-grid">
            <HotDealsSection type="hot_products" title="Hot Products" />
            <HotDealsSection type="todays_deals" title="Today's Deals" />
            <HotDealsSection type="new_arrivals" title="New Arrivals" />
            <HotDealsSection type="exclusive_offers" title="Exclusive Offers" />
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section 
        className="hero" 
        style={{
          background: heroBackgroundImage ? 
            `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${heroBackgroundImage}')` :
            `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Debug info - remove after testing */}
        {console.log('Hero background image URL:', heroBackgroundImage)}
        <div className="hero-content">
          <h2>Welcome to Inifiy</h2>
          <p>Discover millions of products at great prices</p>
          <Link to="/products" className="cta-button">Shop Now</Link>
        </div>
      </section>

      {/* Category Product Sections - New Layout */}
      <section className="categories-showcase">
        <div className="container">
          <div className="categories-showcase-grid">
            <CategoryShowcase category="Electronics" title="Electronics" />
            <CategoryShowcase category="Home & Kitchen" title="Home & Kitchen" />
            <CategoryShowcase category="Personal Care" title="Personal Care" />
            <CategoryShowcase category="Pet Care" title="Pet Care" />
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="featured-products">
        <div className="container">
          <h2>Featured Products</h2>
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading featured products...</p>
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="empty-products">
              <p>No products available yet. Vendors will add products soon!</p>
            </div>
          ) : (
            <div className="products-grid">
              {featuredProducts.map((product) => (
                <div key={product.id} className="product-card-new">
                  <Link to={`/product/${product.id}`} className="product-card-link">
                    <div className="product-card-image">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          loading="lazy"
                        />
                      ) : (
                        <div className="product-placeholder-new">
                          <span>📦</span>
                        </div>
                      )}
                      <div className="product-overlay-new">
                        <div className="product-badges">
                          <span className="badge-featured">Featured</span>
                          {product.category && (
                            <span className="badge-category">{product.category}</span>
                          )}
                        </div>
                        <div className="product-actions-overlay">
                          <button 
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleAddToCart({ 
                                id: product.id, 
                                name: product.name, 
                                price: parseFloat(getPriceForCurrency(product, 'current')),
                                image: product.image_url,
                                vendor_id: product.vendor_id || null
                              });
                            }} 
                            className="quick-add-btn"
                            title="Add to Cart"
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z"/>
                            </svg>
                          </button>
                          <div className="quick-view-btn" title="View Details">
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="product-card-content">
                      <div className="product-header-new">
                        <h3 className="product-title">{product.name}</h3>

                      </div>
                      <p className="product-description-new">
                        {product.description ? 
                          (product.description.length > 60 ? 
                            `${product.description.substring(0, 60)}...` : 
                            product.description
                          ) : 
                          'Amazing product with great features'
                        }
                      </p>
                                              <div className="product-footer-new">
                          <div className="price-section-new">
                            <span className="current-price-new">{getCurrencySymbol()}{parseFloat(getPriceForCurrency(product, 'current')).toFixed(2)}</span>
                            <span className="original-price-new">{getCurrencySymbol()}{parseFloat(getPriceForCurrency(product, 'original')).toFixed(2)}</span>
                            <span className="discount-badge-new">-20%</span>
                          </div>
                      </div>
                    </div>
                  </Link>
                  <button 
                    onClick={() => handleAddToCart({ 
                      id: product.id, 
                      name: product.name, 
                      price: parseFloat(getPriceForCurrency(product, 'current')),
                      image: product.image_url,
                      vendor_id: product.vendor_id || null
                    })} 
                    className="add-to-cart-btn-new"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
                    </svg>
                    Add to Cart
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Download Our App Section */}
      <section className="download-app-section">
        <div className="container">
          <div className="download-app-content">
            <h2>Download Our App</h2>
            <p>Get the Inifiy app for a better shopping experience on the go</p>
            <div className="app-store-buttons">
              <a 
                href="https://play.google.com/store/apps/details?id=com.world.fi.inifiy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="app-store-button google-play"
              >
                <img 
                  src="https://i.postimg.cc/3RR4kQ24/download-3.png" 
                  alt="Get it on Google Play"
                  className="app-store-icon"
                />
              </a>
              <a 
                href="https://apps.apple.com/us/app/inifiy/id6751509322" 
                target="_blank" 
                rel="noopener noreferrer"
                className="app-store-button apple-store"
              >
                <img 
                  src="https://i.postimg.cc/4dSKt218/download-2.png" 
                  alt="Download on the App Store"
                  className="app-store-icon"
                />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Follow Us on Social Media Section */}
      <section className="social-media-section">
        <div className="container">
          <div className="social-media-content">
            <h2>Follow Us on Social Media</h2>
            <p>Stay connected with Inifiy for the latest updates, deals, and product launches</p>
            <div className="social-media-buttons">
              <a 
                href="https://www.facebook.com/profile.php?id=61579113566140" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-media-button facebook"
              >
                <img 
                  src="https://i.postimg.cc/yYtr8V45/download-4.png" 
                  alt="Follow us on Facebook"
                  className="social-icon"
                />
              </a>
              <a 
                href="https://www.instagram.com/inifiy_store?igsh=dmtsb2Jza3hlbnp4" 
                target="_blank" 
                rel="noopener noreferrer"
                className="social-media-button instagram"
              >
                <img 
                  src="https://i.postimg.cc/hPf5nXwT/download-3.jpg" 
                  alt="Follow us on Instagram"
                  className="social-icon"
                />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// CartIcon Component
function CartIcon() {
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  return (
    <Link to="/cart" className="cart-icon">
      <svg className="cart-svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
      </svg>
      {cartCount > 0 && (
        <span className="cart-count">{cartCount}</span>
      )}
    </Link>
  );
}

export default App;
